/**
 * Runtime feature flags.
 *
 * Flags are read once at module load from Vite `import.meta.env`. Each flag has
 * an explicit default — when the env var is unset, the default applies. To turn
 * a flag on/off in a given environment, set the env var to "true"/"false"
 * (case-insensitive, also accepts "1"/"0"/"yes"/"no") in the appropriate .env
 * file or CI config.
 *
 * Production defaults are intentionally conservative: only SSO is on. Other
 * login methods are opt-in for non-prod deployments.
 */

export function parseBool(
  raw: string | undefined,
  defaultValue: boolean,
): boolean {
  if (raw === undefined) return defaultValue;
  const v = raw.trim().toLowerCase();
  if (v === "") return defaultValue;
  if (v === "true" || v === "1" || v === "yes") return true;
  if (v === "false" || v === "0" || v === "no") return false;
  // eslint-disable-next-line no-console
  console.warn(
    `[featureFlags] unrecognised boolean value "${raw}" — falling back to default (${defaultValue}).`,
  );
  return defaultValue;
}

export interface FeatureFlags {
  /** Show the Entra (Azure AD) SSO sign-in button on the login screen. */
  loginSso: boolean;
  /** Show the Pathways B2C (member portal) sign-in form on the login screen. */
  loginPathways: boolean;
  /** Show the Provider Portal B2C sign-in form on the login screen. */
  loginProviderPortal: boolean;
}

export const featureFlags: FeatureFlags = {
  loginSso: parseBool(import.meta.env.VITE_FEATURE_LOGIN_SSO, true),
  loginPathways: parseBool(import.meta.env.VITE_FEATURE_LOGIN_PATHWAYS, false),
  loginProviderPortal: parseBool(
    import.meta.env.VITE_FEATURE_LOGIN_PROVIDER_PORTAL,
    false,
  ),
};
